import Point from './point';
import Segment from './segment';
import { EPSILON } from './constant';
class _MaxError {
    constructor(error, index) {
        this.error = error;
        this.index = index;
    }
}
export default class Simplify {
    constructor(path) {
        this.points = [];
        this.segments = [];
        this.segments = path.segments;
        let prev;
        // Copy over points from path and filter out adjacent duplicates.
        for (let i = 0; i < this.segments.length; i++) {
            const point = this.segments[i].point;
            if (!prev || !prev.equals(point)) {
                prev = point.clone();
                this.points.push(prev);
            }
        }
    }
    fit(error) {
        let segments = [];
        if (this.points.length > 0) {
            segments = [new Segment(this.points[0])];
            if (this.points.length > 1) {
                this.fitCubic(segments, error, 0, this.points.length - 1, this.points[1].subtract(this.points[0]), this.points[this.points.length - 2].subtract(this.points[this.points.length - 1]));
            }
            return this.addIndex(segments);
        }
        return segments;
    }
    addIndex(segments) {
        for (let i = 0; i < segments.length; i++)
            segments[i].index = i;
        return segments;
    }
    // 将贝塞尔曲线拟合到（子）数字化点集
    fitCubic(segments, error, first, last, tan1, tan2) {
        // Parameterize points, and attempt to fit curve
        const uPrime = this.chordLengthParameterize(first, last);
        let maxError = Math.max(error, error * error);
        // TODO
        let split = 0;
        let parametersInOrder = true;
        // Try 4 iterations
        for (let i = 0; i < 4; i++) {
            const curve = this.generateBezier(first, last, uPrime, tan1, tan2);
            // 找到点到拟合曲线的最大偏差
            const max = this.findMaxError(first, last, curve, uPrime);
            if (max.error < error && parametersInOrder) {
                this.addCurve(segments, curve);
                return;
            }
            split = max.index;
            // 如果错误不是太大，尝试重新参数化和迭代
            if (max.error >= maxError)
                break;
            parametersInOrder = this.reparameterize(first, last, uPrime, curve);
            maxError = max.error;
        }
        // 拟合失败 - 在最大错误点分割并递归拟合
        const tanCenter = this.points[split - 1].subtract(this.points[split + 1]);
        this.fitCubic(segments, error, first, split, tan1, tanCenter);
        this.fitCubic(segments, error, split, last, tanCenter.negate(), tan2);
    }
    // Assign parameter values to digitized points
    // using relative distances between points.
    chordLengthParameterize(first, last) {
        const u = [];
        u[0] = 0;
        for (let i = first + 1; i <= last; i++) {
            u[i - first] = u[i - first - 1] + this.points[i].getDistance(this.points[i - 1]);
        }
        const m = last - first;
        for (let i = 0; i <= m; i++) {
            u[i] /= u[m];
        }
        return u;
    }
    // 使用最小二乘法找到区域的 贝塞尔控制点。
    // 最小二乘法 https://blog.csdn.net/nick7246/article/details/78164863
    generateBezier(first, last, uPrime, tan1, tan2) {
        const pt1 = this.points[first];
        const pt2 = this.points[last];
        const C = [[0, 0], [0, 0]];
        const X = [0, 0];
        for (let i = 0; i < last - first + 1; i++) {
            const u = uPrime[i];
            const t = 1 - u;
            const b = 3 * u * t;
            const b0 = t * t * t;
            const b1 = b * t;
            const b2 = b * u;
            const b3 = u * u * u;
            const a1 = tan1.normalize(b1);
            const a2 = tan2.normalize(b2);
            const tmp = this.points[first + i]
                .subtract(pt1.multiply(new Point(b0 + b1, b0 + b1)))
                .subtract(pt2.multiply(new Point(b2 + b3, b2 + b3)));
            C[0][0] += a1.dot(a1);
            C[0][1] += a1.dot(a2);
            C[1][0] = C[0][1];
            C[1][1] += a2.dot(a2);
            X[0] += a1.dot(tmp);
            X[1] += a2.dot(tmp);
        }
        // 计算C和X的决定因素
        const detC0C1 = C[0][0] * C[1][1] - C[1][0] * C[0][1];
        let alpha1;
        let alpha2;
        if (Math.abs(detC0C1) > EPSILON) {
            // Kramer's rule
            // 克莱姆法则
            // https://zh.wikipedia.org/wiki/%E5%85%8B%E8%90%8A%E5%A7%86%E6%B3%95%E5%89%87
            var detC0X = C[0][0] * X[1] - C[1][0] * X[0], detXC1 = X[0] * C[1][1] - X[1] * C[0][1];
            // 导出alpha值
            alpha1 = detXC1 / detC0C1;
            alpha2 = detC0X / detC0C1;
        }
        else {
            const c0 = C[0][0] + C[0][1];
            const c1 = C[1][0] + C[1][1];
            alpha1 = alpha2 = Math.abs(c0) > EPSILON ? X[0] / c0
                : Math.abs(c1) > EPSILON ? X[1] / c1
                    : 0;
        }
        // alpha = 阿尔法 α
        // 如果alpha为负，请使用Wu / Barsky启发式
        // 如果alpha为0，则会得到重合控制点，
        // 导致在任何后续 NewtonRaphsonRootFind() 调用中除以零。
        const segLength = pt2.getDistance(pt1);
        const eps = EPSILON * segLength;
        let handle1;
        let handle2;
        if (alpha1 < eps || alpha2 < eps) {
            // 回到标准（可能是不准确的）公式，并在需要时进一步细分。
            alpha1 = alpha2 = segLength / 3;
        }
        else {
            // 通过 pt1 和 pt2 投影到线路上时，检查找到的控制点是否按正确顺序排列。
            const line = pt2.subtract(pt1);
            // 控制 点1 和 2 分别位于切线向量上的左侧和右侧的 alpha 距离
            handle1 = tan1.normalize(alpha1);
            handle2 = tan2.normalize(alpha2);
            if (handle1.dot(line) - handle2.dot(line) > segLength * segLength) {
                // 回到上面的Wu / Barsky启发式。
                alpha1 = alpha2 = segLength / 3;
                // 强制重新计算
                handle1 = handle2 = undefined;
            }
        }
        // 贝塞尔曲线的第一个和最后一个控制点正好位于第一个和最后一个数据点
        return [
            pt1,
            pt1.add(handle1 || tan1.normalize(alpha1)),
            pt2.add(handle2 || tan2.normalize(alpha2)),
            pt2
        ];
    }
    // 找到数字化点到拟合曲线的最大平方距离。
    findMaxError(first, last, curve, u) {
        let index = Math.floor((last - first + 1) / 2);
        let maxDist = 0;
        for (let i = first + 1; i < last; i++) {
            const P = this.evaluate(3, curve, u[i - first]);
            const v = P.subtract(this.points[i]);
            const dist = v.x * v.x + v.y * v.y; // squared
            if (dist >= maxDist) {
                maxDist = dist;
                index = i;
            }
        }
        return new _MaxError(maxDist, index);
    }
    // 评估特定参数值的贝塞尔曲线
    evaluate(degree, curve, t) {
        const tmp = curve.slice();
        // 三角计算
        for (var i = 1; i <= degree; i++) {
            for (var j = 0; j <= degree - i; j++) {
                tmp[j] = tmp[j].multiply(new Point(1 - t, 1 - t)).add(tmp[j + 1].multiply(new Point(t, t)));
            }
        }
        return tmp[0];
    }
    addCurve(segments, curve) {
        // 最后一个
        const prev = segments[segments.length - 1];
        prev.handleOut = curve[1].subtract(curve[0]);
        segments.push(new Segment(curve[3], curve[2].subtract(curve[3])));
    }
    // 给定一组点及其参数化，尝试找到更好的参数化。
    reparameterize(first, last, u, curve) {
        for (let i = first; i <= last; i++) {
            u[i - first] = this.findRoot(curve, this.points[i], u[i - first]);
        }
        // 检测新参数化是否重新排序了这些点
        // 在这种情况下，我们会以错误的顺序拟合路径的点。
        for (let i = 1, l = u.length; i < l; i++) {
            if (u[i] != undefined && u[i - 1] != undefined && u[i] <= u[i - 1])
                return false;
        }
        return true;
    }
    // 使用 牛顿法 迭代来找到更好的根。
    // 牛顿法 https://zh.wikipedia.org/w/index.php?title=%E7%89%9B%E9%A1%BF%E6%B3%95&oldid=47516868
    findRoot(curve, point, u) {
        const curve1 = [];
        const curve2 = [];
        // 生成 Q' 的控制顶点
        for (let i = 0; i <= 2; i++) {
            curve1[i] = curve[i + 1].subtract(curve[i]).multiply(new Point(3, 3));
        }
        // 生成 Q'' 的控制顶点
        for (let i = 0; i <= 1; i++) {
            curve2[i] = curve1[i + 1].subtract(curve1[i]).multiply(new Point(2, 2));
        }
        // 比较Q(u), Q'(u), Q''(u)
        const pt = this.evaluate(3, curve, u);
        const pt1 = this.evaluate(2, curve1, u);
        const pt2 = this.evaluate(1, curve2, u);
        const diff = pt.subtract(point);
        const df = pt1.dot(pt1) + diff.dot(pt2);
        // u = u - f(u) / f'(u)
        return this.isZero(df) ? u : u - diff.dot(pt1) / df;
    }
    isZero(val) {
        return val >= -EPSILON && val <= EPSILON;
    }
}
//# sourceMappingURL=simplify.js.map